"use client";

import { useEffect, useState } from "react";
import { Cloud } from "lucide-react";
import cloud from "d3-cloud";

import { t } from "@/lib/localization";
import { WordCloudBlock as WordCloudBlockType } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";
import RichText from "../text/richText";
import { PreviewButton } from "../shared/PreviewButton";
import RichHtml from "./richHtml";
import { seedTitleHtml, stripHtml } from "../../../lib/titleHtml";
import { fieldTag } from "../../../lib/sectionRegistry";
import { DEFAULT_THEME } from "../../../lib/theme";
import {
    useBlockAggregate,
    useSubmitBlockResponse,
} from "../../../api/useBlockResponses";

// Varied palette (theme primary + accents) for the classic multi-colour look.
const PALETTE = [
    "hsl(var(--lomda-primary))",
    "hsl(var(--lomda-secondary))",
    "hsl(var(--lomda-success))",
    "hsl(var(--lomda-destructive))",
    "hsl(var(--lomda-text))",
    "hsl(var(--lomda-border-active))",
    "hsl(var(--lomda-muted-text))",
];
const CLOUD_W = 700;
const CLOUD_H = 400;
const WORD_WEIGHT = 700;
const FONT_WAIT_MS = 3000;

type Placed = {
    text: string;
    size: number;
    x: number;
    y: number;
    rotate: number;
};

/**
 * A classic packed word cloud (d3-cloud): each word is placed by the layout,
 * sized by how often it was submitted across learners, some rotated 90°.
 */
function WordCloudView({ words }: { words: { text: string; count: number }[] }) {
    // Drawn in the font it was measured in.
    const [cloudOut, setCloudOut] = useState<{
        fontFamily: string;
        words: Placed[];
    } | null>(null);
    const font =
        useLomdaV3Editor((s) => s.metadata.theme?.fontBody) ||
        DEFAULT_THEME.fontBody;

    useEffect(() => {
        if (words.length === 0) return;
        const fontFamily = `"${font}", sans-serif`;
        const counts = words.map((w) => w.count);
        const max = Math.max(...counts);
        const min = Math.min(...counts);
        const sizeFor = (count: number) =>
            max === min ? 36 : 18 + ((count - min) / (max - min)) * 52;

        let layout: ReturnType<typeof cloud> | null = null;
        let cancelled = false;
        const run = () => {
            layout?.stop();
            layout = cloud()
                .size([CLOUD_W, CLOUD_H])
                .words(
                    words.map((w) => ({ text: w.text, size: sizeFor(w.count) }))
                )
                .padding(3)
                .rotate(() => (Math.random() < 0.72 ? 0 : 90))
                .font(fontFamily)
                .fontWeight(WORD_WEIGHT)
                .fontSize((d) => d.size ?? 18)
                .on("end", (out) =>
                    setCloudOut({
                        fontFamily,
                        words: out.map((w) => ({
                            text: w.text ?? "",
                            size: w.size ?? 18,
                            x: w.x ?? 0,
                            y: w.y ?? 0,
                            rotate: w.rotate ?? 0,
                        })),
                    })
                );
            layout.start();
        };

        const fonts = typeof document !== "undefined" ? document.fonts : null;
        if (!fonts) {
            run();
            return () => layout?.stop();
        }

        // Words measured in a fallback font overlap once the real one swaps
        // in, so the final layout waits for its faces. With none to wait for
        // it lays out now, and again if the family registers later.
        const spec = `${WORD_WEIGHT} 36px ${fontFamily}`;
        const text = words.map((w) => w.text).join("");
        let laidOut = false;
        let settled = false;
        const layoutOnce = () => {
            if (cancelled || settled || laidOut) return;
            laidOut = true;
            run();
        };
        const fallback = setTimeout(layoutOnce, FONT_WAIT_MS);
        function settle() {
            if (cancelled || settled) return;
            settled = true;
            clearTimeout(fallback);
            fonts?.removeEventListener("loadingdone", check);
            run();
        }
        const registered = () => {
            let found = false;
            fonts?.forEach((face) => {
                if (face.family.replace(/^["']|["']$/g, "") === font)
                    found = true;
            });
            return found;
        };
        function check() {
            fonts?.load(spec, text).then((faces) => {
                if (faces.length > 0 || registered()) settle();
                else {
                    clearTimeout(fallback);
                    layoutOnce();
                }
            }, settle);
        }
        fonts.addEventListener("loadingdone", check);
        check();
        return () => {
            cancelled = true;
            clearTimeout(fallback);
            fonts.removeEventListener("loadingdone", check);
            layout?.stop();
        };
    }, [words, font]);

    if (!cloudOut?.words.length) return null;

    return (
        <svg
            viewBox={`0 0 ${CLOUD_W} ${CLOUD_H}`}
            className="h-auto w-full animate-in fade-in"
        >
            <g transform={`translate(${CLOUD_W / 2},${CLOUD_H / 2})`}>
                {cloudOut.words.map((w, i) => (
                    <text
                        key={`${w.text}-${i}`}
                        textAnchor="middle"
                        transform={`translate(${w.x},${w.y}) rotate(${w.rotate})`}
                        fontSize={w.size}
                        fontWeight={WORD_WEIGHT}
                        fontFamily={cloudOut.fontFamily}
                        fill={PALETTE[i % PALETTE.length]}
                    >
                        {w.text}
                    </text>
                ))}
            </g>
        </svg>
    );
}

export default function WordCloudBlock({
    block,
}: {
    block: WordCloudBlockType;
}) {
    const questionTag = fieldTag("word-cloud", "question");
    const editable = useBlockEditable();
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const lomdaId = useLomdaV3Editor((s) => s.lomdaId);
    const lomdaLocale = useLomdaLocale();
    const { question, wordCount } = block.data;

    const [inputs, setInputs] = useState<string[]>([]);
    const [submitted, setSubmitted] = useState(false);
    const submit = useSubmitBlockResponse();
    const aggregate = useBlockAggregate(block.id, !editable && submitted);

    const update = (patch: Partial<WordCloudBlockType["data"]>) =>
        replaceBlockData(block.id, { ...block.data, ...patch });

    if (editable) {
        return (
            <div className="lomda-v3-card-surface flex flex-col gap-3 rounded-[20px] border border-[hsl(var(--lomda-border))] p-6">
                <RichText
                    initialHtml={seedTitleHtml(question, questionTag)}
                    defaultTag={questionTag}
                    editable
                    useGlobalToolbar
                    blockId={block.id}
                    placeholder={t(
                        "lomda-v3-editor.word-cloud.question-placeholder"
                    )}
                    onCommit={(html) => update({ question: html })}
                />
                {/* Learner enters {wordCount} words; the count is set on the topbar. */}
                <div className="flex flex-col gap-2.5 opacity-70">
                    {Array.from({ length: wordCount }, (_, i) => (
                        <div
                            key={i}
                            className="rounded-xl border border-dashed border-[hsl(var(--lomda-border))] bg-[hsl(var(--lomda-background))] px-4 py-3 lomda-v3-text-p text-[hsl(var(--lomda-muted-text))]"
                        >
                            {t("lomda-v3-editor.word-cloud.word-placeholder", lomdaLocale, { number: i + 1 })}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (!stripHtml(question)) return null;

    if (submitted) {
        const words = (aggregate.data?.words ?? []) as {
            text: string;
            count: number;
        }[];
        return (
            <div className="lomda-v3-card-surface flex flex-col gap-4 rounded-[20px] border border-[hsl(var(--lomda-border))] p-6">
                <RichHtml html={seedTitleHtml(question, questionTag)} />
                <div className="rounded-2xl bg-[hsl(var(--lomda-background))]">
                    {words.length === 0 ? (
                        <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
                            <Cloud className="size-10 animate-pulse text-[hsl(var(--lomda-primary))]" />
                            <span className="lomda-v3-text-p text-[hsl(var(--lomda-muted-text))]">
                                {t("lomda-v3-editor.word-cloud.generating", lomdaLocale)}
                            </span>
                        </div>
                    ) : (
                        <WordCloudView words={words} />
                    )}
                </div>
            </div>
        );
    }

    const doSubmit = () => {
        const words = inputs.map((w) => w?.trim()).filter(Boolean) as string[];
        if (words.length === 0) return;
        setSubmitted(true);
        submit.mutate(
            {
                lomdaId,
                blockId: block.id,
                type: "word-cloud",
                payload: { words },
            },
            { onSuccess: () => aggregate.refetch() }
        );
    };

    const canSubmit = inputs.some((w) => w?.trim());

    return (
        <div className="lomda-v3-card-surface flex flex-col gap-4 rounded-[20px] border border-[hsl(var(--lomda-border))] p-6">
            <RichHtml html={seedTitleHtml(question, questionTag)} />
            <div className="flex flex-col gap-2.5">
                {Array.from({ length: wordCount }, (_, i) => (
                    <input
                        key={i}
                        value={inputs[i] ?? ""}
                        onChange={(e) =>
                            setInputs((prev) => {
                                const next = [...prev];
                                next[i] = e.target.value;
                                return next;
                            })
                        }
                        placeholder={t(
                            "lomda-v3-editor.word-cloud.word-placeholder",
                            lomdaLocale,
                            { number: i + 1 }
                        )}
                        className="lomda-v3-page-text w-full rounded-xl border border-[hsl(var(--lomda-border))] bg-[hsl(var(--lomda-background))] px-4 py-3 lomda-v3-text-p outline-none placeholder:text-[hsl(var(--lomda-muted-text))] focus:border-[hsl(var(--lomda-primary))]"
                    />
                ))}
            </div>
            <PreviewButton type="button" disabled={!canSubmit} onClick={doSubmit}>
                {t("lomda-v3-editor.word-cloud.submit", lomdaLocale)}
            </PreviewButton>
        </div>
    );
}
